import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import {
  formatEditablePhoneNumber,
  validatePhoneNumber,
} from '@careerstart/wae-common/src/main/helperFunction/profileHelperss';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';

import theme from '../../../theme';
import { BLACK, PRIMARY_PURPLE } from '../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../theme/fontConstants';

const styles = theme?.components?.userCard;

const EditablePhoneField = ({ initialValue, userId, disabled, isLoading, onPhoneSave }) => {
  const [phoneNumber, setPhoneNumber] = useState(
    initialValue ? formatEditablePhoneNumber(initialValue) : ''
  );
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Only update local state when:
    // 1. initialValue is defined (not undefined/null - distinguishes from loading state)
    // 2. Not currently processing an update (to avoid race conditions)
    // 3. Not currently editing (to avoid overwriting user input)
    if (initialValue !== undefined && initialValue !== null && !isLoading && !isEditing) {
      const formattedValue = formatEditablePhoneNumber(initialValue);
      setPhoneNumber((prevPhoneNumber) => {
        // Only update if the value has actually changed (to avoid unnecessary updates)
        if (prevPhoneNumber !== formattedValue) {
          return formattedValue;
        }
        return prevPhoneNumber;
      });
    }
  }, [initialValue, isLoading, isEditing]);

  const handlePhoneChange = (e) => {
    const { value } = e.target;
    if (!/^[()\d\s-]*$/.test(value)) return;
    const digits = value.replace(/\D/g, '');
    if (digits.length > 10) return;
    setPhoneNumber(formatEditablePhoneNumber(value));
    setIsEditing(true);
    setError('');
  };

  const handlePhoneSave = useCallback(() => {
    const validationError = validatePhoneNumber(phoneNumber);
    setError(validationError);

    if (!validationError) {
      const rawPhoneNumber = phoneNumber.replace(/\D/g, '');
      const formattedInitialValue = initialValue ? formatEditablePhoneNumber(initialValue) : '';

      // Only update if the phone number has actually changed
      if (phoneNumber !== formattedInitialValue) {
        onPhoneSave({ userId, phoneNumber: rawPhoneNumber });
      }
      setIsEditing(false);
    }
  }, [phoneNumber, initialValue, userId, onPhoneSave]);

  return (
    <Box display="flex" flexDirection="column">
      <Typography sx={styles.subTitleStyle}>
        {LanguageConverterV2('userCard.subTitle.phone')}
      </Typography>
      <Box sx={styles.phoneContainer}>
        <TextField
          disabled={disabled}
          value={phoneNumber}
          onChange={handlePhoneChange}
          placeholder={LanguageConverterV2('userCard.phone.placeholder')}
          variant="outlined"
          inputProps={{
            maxLength: 14,
            'data-testid': 'phone-input',
          }}
          sx={styles.phoneInput}
        />
        <Button
          variant="outlined"
          onClick={handlePhoneSave}
          data-testid="phone-save-button"
          disabled={!isEditing || disabled || isLoading}
          sx={{
            color: isEditing ? PRIMARY_PURPLE[70] : BLACK[60],
            fontFamily: PRIMARY_FONT[500],
            borderColor: isEditing ? PRIMARY_PURPLE[70] : BLACK[60],
            height: '40px',
          }}
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
        >
          {LanguageConverterV2('buttonText.save')}
        </Button>
      </Box>
      {error && (
        <Typography data-testid="phone-error-message" sx={styles.errorMessage}>
          {LanguageConverterV2(error)}
        </Typography>
      )}
    </Box>
  );
};

EditablePhoneField.propTypes = {
  initialValue: PropTypes.string,
  userId: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool.isRequired,
  onPhoneSave: PropTypes.func.isRequired,
};

export default EditablePhoneField;
